/**
 * Calendar utility functions for Bavarian holidays and date calculations
 */

/**
 * Calculate Easter Sunday using Anonymous Gregorian algorithm
 */
export const getEasterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
};

/**
 * Get Bavarian public holidays for a given year
 */
export const getBavarianHolidays = (year: number): Set<string> => {
  const holidays = new Set<string>();
  const addHoliday = (month: number, day: number) => {
    holidays.add(`${year}-${month}-${day}`);
  };

  // Fixed holidays
  addHoliday(0, 1); // Neujahr
  addHoliday(0, 6); // Heilige Drei Könige
  addHoliday(4, 1); // Tag der Arbeit
  addHoliday(7, 15); // Mariä Himmelfahrt
  addHoliday(9, 3); // Tag der Deutschen Einheit
  addHoliday(10, 1); // Allerheiligen
  addHoliday(11, 25); // 1. Weihnachtstag
  addHoliday(11, 26); // 2. Weihnachtstag

  // Easter-based holidays
  const easter = getEasterSunday(year);
  const addEasterOffset = (offset: number) => {
    const d = new Date(easter);
    d.setDate(d.getDate() + offset);
    holidays.add(`${year}-${d.getMonth()}-${d.getDate()}`);
  };

  addEasterOffset(-2); // Karfreitag
  addEasterOffset(0); // Ostersonntag
  addEasterOffset(1); // Ostermontag
  addEasterOffset(39); // Christi Himmelfahrt
  addEasterOffset(49); // Pfingstsonntag
  addEasterOffset(50); // Pfingstmontag
  addEasterOffset(60); // Fronleichnam

  return holidays;
};

/**
 * Check if a specific date is a Bavarian public holiday
 */
export const isHoliday = (year: number, month: number, day: number): boolean => {
  const holidays = getBavarianHolidays(year);
  return holidays.has(`${year}-${month}-${day}`);
};

/**
 * Get the number of days in a month
 */
export const getDaysInMonth = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

/**
 * Get the first day of the month (Monday = 0, Sunday = 6)
 */
export const getFirstDayOfMonth = (date: Date): number => {
  const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  return day === 0 ? 6 : day - 1;
};

/**
 * Calendar day data for rendering
 */
export interface CalendarDay {
  day: number;
  month: number;
  year: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  isHoliday: boolean;
}

/**
 * Generate calendar grid data with fixed 5 rows (35 cells)
 * Includes days from previous and next months to fill the grid
 */
export const getCalendarGrid = (viewDate: Date, today: Date = new Date()): CalendarDay[] => {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = getDaysInMonth(viewDate);
  const firstDay = getFirstDayOfMonth(viewDate);

  // Previous month info
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

  // Next month info
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;

  const days: CalendarDay[] = [];
  const TOTAL_CELLS = 35; // 5 rows x 7 days

  // Add days from previous month
  for (let i = firstDay - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    const dayOfWeek = (firstDay - i - 1 + 7) % 7;
    days.push({
      day,
      month: prevMonth,
      year: prevYear,
      isCurrentMonth: false,
      isToday: false,
      isWeekend: dayOfWeek === 5 || dayOfWeek === 6,
      isHoliday: isHoliday(prevYear, prevMonth, day),
    });
  }

  // Add days from current month
  for (let day = 1; day <= daysInMonth; day++) {
    const dayOfWeek = (firstDay + day - 1) % 7;
    const isTodayDate =
      day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

    days.push({
      day,
      month,
      year,
      isCurrentMonth: true,
      isToday: isTodayDate,
      isWeekend: dayOfWeek === 5 || dayOfWeek === 6,
      isHoliday: isHoliday(year, month, day),
    });
  }

  // Add days from next month to fill remaining cells
  let nextDay = 1;
  while (days.length < TOTAL_CELLS) {
    const dayOfWeek = days.length % 7;
    days.push({
      day: nextDay,
      month: nextMonth,
      year: nextYear,
      isCurrentMonth: false,
      isToday: false,
      isWeekend: dayOfWeek === 5 || dayOfWeek === 6,
      isHoliday: isHoliday(nextYear, nextMonth, nextDay),
    });
    nextDay++;
  }

  return days;
};
